import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { Reveal, PageHero } from './LandingShared';
import { useTranslation } from '../contexts/TranslationContext';
import { BookOpenIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';

/**
 * InvestorResources Component
 * Displays a grid of links to external investor resource websites.
 * Styled with Tailwind CSS for a modern, interactive, and professional card-based layout.
 */
const InvestorResources = () => {
    const { t } = useTranslation();
    const resources = [
        { name: "Bloomberg", url: "http://www.bloomberg.com/" },
        { name: "Investor.com", url: "https://www.investors.com/" },
        { name: "Morningstar", url: "https://www.morningstar.com/" },
        { name: "Research Affiliates", url: "https://www.researchaffiliates.com/home" },
        { name: "Reuters", url: "https://www.researchaffiliates.com/home" },
        { name: "The Market Ear", url: "https://themarketear.com/newsfeed" },
        { name: "Briefing.com", url: "https://www.briefing.com/stock-market-update" },
        { name: "Trading Economics", url: "https://tradingeconomics.com/" },
        { name: "Seeking Alpha", url: "https://seekingalpha.com/" },
        { name: "Cbonds", url: "https://cbonds.com/news/" },
        { name: "Fast Graphs", url: "https://fastgraphs.com/" },
        { name: "etf.com", url: "https://www.etf.com/" },
        { name: "macrotrends", url: "https://www.macrotrends.net/" },
    ];

    return (
        <div className="flex flex-col min-h-screen bg-white">
            <Helmet>
              <title>Investor Resources — Cogent Platform</title>
              <meta name="description" content="Access curated investor resources including Bloomberg, Reuters, Morningstar, Seeking Alpha, and more. Stay informed with top financial research and market data." />
              <link rel="canonical" href="https://cogentplatform.com/investor-resources" />
            </Helmet>
            <Navbar />

            <main className="flex-grow">
                {/* Hero Banner */}
                <PageHero
                    title={t('investorResources.title')}
                    subtitle={t('investorResources.subtitle')}
                />

                {/* Content Section */}
                <div className="bg-gradient-to-b from-white to-slate-50 py-16 sm:py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                            {resources.map((resource, i) => (
                                <Reveal key={i} delay={i * 80}>
                                    <a
                                        href={resource.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group block rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-blue-200 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                                    >
                                        <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />
                                        <div className="p-6 sm:p-8">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-md">
                                                        <BookOpenIcon className="h-6 w-6 text-white" />
                                                    </div>
                                                    <h2 className="ml-4 text-lg font-bold text-gray-900">
                                                        {resource.name}
                                                    </h2>
                                                </div>
                                                <ArrowTopRightOnSquareIcon className="h-6 w-6 text-gray-300 group-hover:text-blue-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                                            </div>
                                        </div>
                                    </a>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default InvestorResources;
